import { useParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { sbomApi, sourcesApi } from '@/api/clients/apiClient'
import { useOrgStore } from '@/stores/orgStore'

function formatDate(iso: string) {
  return new Date(iso).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export default function SbomHistory() {
  const { id: sourceId } = useParams<{ id: string }>()
  const { currentOrgId } = useOrgStore()

  const { data: sourceResp } = useQuery({
    queryKey: ['source', currentOrgId, sourceId],
    queryFn: () => sourcesApi.get(currentOrgId, sourceId!),
    enabled: !!currentOrgId && !!sourceId,
  })
  const source = sourceResp?.data

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['sbom-versions', currentOrgId, sourceId],
    queryFn: () => sbomApi.getSbomVersions(currentOrgId, sourceId!),
    enabled: !!currentOrgId && !!sourceId,
  })

  const versions = data?.versions ?? []

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-subtle-light dark:text-subtle-dark">
        <Link to="/sources" className="hover:text-primary">Discovery Sources</Link>
        <span className="material-icons text-base">chevron_right</span>
        {source ? (
          <Link to={`/sources/${sourceId}`} className="hover:text-primary">{source.name}</Link>
        ) : (
          <span>{sourceId?.slice(0, 8)}…</span>
        )}
        <span className="material-icons text-base">chevron_right</span>
        <span className="text-text-light dark:text-text-dark">SBOM History</span>
      </div>

      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-text-light dark:text-text-dark">SBOM History</h1>
          {source && (
            <p className="text-sm text-subtle-light dark:text-subtle-dark mt-0.5">{source.name}</p>
          )}
        </div>
        <div className="text-xs text-subtle-light dark:text-subtle-dark bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] rounded-lg px-3 py-2">
          {versions.length} version{versions.length !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Compliance note */}
      <div className="flex items-start gap-3 p-4 bg-primary/5 dark:bg-primary/10 border border-primary/20 rounded-xl text-sm text-text-light dark:text-text-dark">
        <span className="material-icons text-primary text-xl mt-0.5">verified_user</span>
        <p>
          SBOM versions are retained per compliance retention policy. Each version is immutably sealed
          and linked to the originating scan job.
        </p>
      </div>

      {/* Error */}
      {isError && (
        <div className="flex items-start gap-3 p-4 bg-error/5 border border-error/20 rounded-xl">
          <span className="material-icons text-error text-xl mt-0.5">error_outline</span>
          <div>
            <p className="text-sm font-medium text-error">Failed to load SBOM history</p>
            <p className="text-sm text-error/80 mt-0.5 font-mono">{(error as Error)?.message}</p>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] rounded-xl shadow-ambient dark:shadow-none border border-border-light dark:border-border-dark overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-container-light dark:bg-container-dark">
            <tr>
              <th className="px-6 py-3 text-left label-sm text-subtle-light dark:text-subtle-dark">Version</th>
              <th className="px-6 py-3 text-left label-sm text-subtle-light dark:text-subtle-dark">Created</th>
              <th className="px-6 py-3 text-left label-sm text-subtle-light dark:text-subtle-dark hidden sm:table-cell">Components</th>
              <th className="px-6 py-3 text-left label-sm text-subtle-light dark:text-subtle-dark hidden md:table-cell">Scan Job</th>
              <th className="px-6 py-3 text-right label-sm text-subtle-light dark:text-subtle-dark">Export</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={5} className="px-6 py-10 text-center">
                  <span className="material-icons animate-spin text-primary text-2xl">sync</span>
                </td>
              </tr>
            ) : versions.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-6 py-10 text-center text-subtle-light dark:text-subtle-dark">
                  <div className="flex flex-col items-center gap-2">
                    <span className="material-icons text-4xl opacity-40">history</span>
                    <p>No SBOM versions found. Run a scan to generate the first version.</p>
                  </div>
                </td>
              </tr>
            ) : (
              versions.map((v, i) => (
                <tr
                  key={v.id}
                  className={`border-t border-border-light dark:border-border-dark hover:bg-background-light dark:hover:bg-background-dark ${i === 0 ? 'font-medium' : ''}`}
                >
                  <td className="px-6 py-3">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-text-light dark:text-text-dark">v{v.version}</span>
                      {i === 0 && (
                        <span className="text-xs px-1.5 py-0.5 rounded bg-primary/10 text-primary">latest</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-3 text-subtle-light dark:text-subtle-dark">
                    {formatDate(v.created_at)}
                  </td>
                  <td className="px-6 py-3 text-subtle-light dark:text-subtle-dark hidden sm:table-cell">
                    {v.component_count.toLocaleString()}
                  </td>
                  <td className="px-6 py-3 hidden md:table-cell">
                    <Link
                      to={`/scans/${v.scan_job_id}`}
                      className="font-mono text-xs text-primary hover:underline"
                    >
                      {v.scan_job_id.slice(0, 8)}…
                    </Link>
                  </td>
                  <td className="px-6 py-3">
                    <div className="flex items-center justify-end gap-1.5">
                      {(['cyclonedx', 'spdx', 'swid'] as const).map((fmt) => (
                        <button
                          key={fmt}
                          onClick={() => sbomApi.downloadScanSbom(currentOrgId, v.scan_job_id, fmt)}
                          title={`Export ${fmt}`}
                          className="px-2 py-1 text-xs rounded hover:border-primary hover:text-primary text-subtle-light dark:text-subtle-dark transition-colors"
                        >
                          {fmt === 'cyclonedx' ? 'CDX' : fmt.toUpperCase()}
                        </button>
                      ))}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
